import { useQuery } from '@tanstack/react-query';
import { CTA, DisplayTag, styled } from '@volkswagen-onehub/components-core';
import { FocusLayerSizeV2 } from '@volkswagen-onehub/layer-manager';
import { Province, VehicleCategory } from '@vw-marketing/vw-ca-types';
import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { LayerContext, useFocusLayer } from '../../Layers';
import { getLang, getTranslations } from '../../translations';
import { ModelKey } from '../../types/offers';
import { useFeatureServices } from '../../use-feature-services';
import { UserLocationContext } from '../../UserLocation/UserLocationContext';
import { fetchOffers } from '../../api/fetchOffers';
import getModelKey from '../../utils/get-model-key';
import { getOffer } from '../../utils/get-offer';
import { getStep } from '../../utils/get-step';
import { createConfiguration, createConstantData, createPartner, createProcess } from '../../utils/get-tracking-event-data';
import { VehicleContext } from '../../utils/vehicle-context';
import OffersModal from '../Offers/OffersModal';
import Skeleton from 'react-loading-skeleton';
import { useDealersStore } from '../../utils/use-dealers-store';
import { AppContext } from '../../app-context';

const StyledCTA = styled(CTA)`
  white-space: nowrap;
  :focus, :hover {
    div {
      color: #ffffff;
      background-color: #0077CC;
    }
  }
`;

export const Flags = () => {
  const route = useLocation();
  const lang = getLang();
  const { labels } = getTranslations();
  const { tracking } = useFeatureServices();
  const { globalApi } = useContext(AppContext);
  const vehicle = useContext(VehicleContext);
  const { province } = useContext(UserLocationContext);

  const { data: offer, isLoading } = useQuery({
    queryKey: ['offers', province],
    queryFn: () => fetchOffers(globalApi, lang, { province: province as Province }),
    select: (data) => vehicle && getOffer(data, getModelKey(vehicle.model_name) as ModelKey, String(vehicle.year), vehicle.model_code),
    enabled: !!province
  });

  const { getDealer } = useDealersStore();
  const dealer = getDealer(vehicle?.dealer_id as string);

  const { closeLayer } = useContext(LayerContext);
  const openOffersLayer = useFocusLayer('offers-layer', {
    size: FocusLayerSizeV2.C,
    onClose: () => closeLayer('offers-layer')
  });

  if (!vehicle || vehicle.category !== VehicleCategory.NEW) return null;

  const step = getStep(route.pathname);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    openOffersLayer((close) => <OffersModal close={close} source={["inventory", "search results page"]} offer={offer!} vehicle={vehicle} dealer={dealer}/>);

    tracking.track('VWBasic_VehicleButton_Click', {
      ...createConstantData('VWBasic_VehicleButton_Click', vehicle.category),
      configuration: createConfiguration('VWBasic_VehicleButton_Click', vehicle, lang),
      partner: createPartner(dealer),
      process: createProcess(step),
      link: {
        url: window.location.href,
        name: labels.view_offers,
        isDealerPage: false
      }
    });
  };

  if (isLoading) {
    return <Skeleton width={115} height={24} />
  }

  return offer ? (
    <StyledCTA tag="button" emphasis="none" onClick={e => handleClick(e)}>
      <DisplayTag appearance="offer" text={labels.view_offers} />
    </StyledCTA>
  ) : null;
};
